import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";

import { shouldGateThirdPartyApplyWithPassport } from "../../../../utils/autoClickApplyUtils";
import { SuccessfulSignInModal } from "./SuccessfulSignInModal.web";

export enum Branding {
  THIRD_PARTY_APPLIES = "third-party-applies",
}

export interface PassportLoginModalProps {
  isOpen: boolean;
  onExit: () => void;
  fromParameter?: string;
  onAuthSuccess?: (e?: CustomEvent) => void | Promise<void>;
  branding?: Branding;
  contextualSignInPassportURL: string;
}

declare global {
  interface Window {
    _INDEED_GNAV?: {
      loginModal?: {
        open: (params: object) => void;
        close: () => void;
      };
    };
  }
}

export interface UsePassportLoginGateOptions {
  applyLink: string | undefined;
  isLoggedIn: boolean;
  contextualSignInPassportURL: string;
  onApply: () => Promise<void>;
  mobtk?: string;
  // Desktop 2-pane (rnViewJobDisplay === 'DESKTOP_EMBEDDED'). The viewjob lives
  // inside the embedded SERP pane, which can't host the in-page GNAV modal +
  // return-to-apply flow, so 2-pane gates via a full redirect to Passport instead.
  isDesktopEmbedded?: boolean;
  jobKey?: string;
}

export function usePassportLoginGate(options: UsePassportLoginGateOptions): {
  isLoginGateActive: boolean;
  loginGateHref: string | undefined;
  onPressApply: () => Promise<void>;
  modal: ReactNode;
} {
  const {
    applyLink,
    isLoggedIn,
    contextualSignInPassportURL,
    onApply,
    mobtk,
    isDesktopEmbedded = false,
    jobKey,
  } = options;

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const hasProcessedUrlParam = useRef(false);

  // Gate logged-out users whenever the backend supplies a Passport sign-in URL.
  // contextualSignInPassportURL is the canonical "show the third-party apply
  // intercept" signal across all traffic sources (GFJ, organic) and platforms,
  // so desktop and mobile gate on the same value. Shares the predicate with the
  // GFJ auto-click gate (see getAutoClickUrl) so the manual click and auto-click
  // entry points can never disagree.
  const isLoginGateActive = shouldGateThirdPartyApplyWithPassport({
    isLoggedIn,
    passportUrl: contextualSignInPassportURL,
  });

  // Desktop 2-pane gates via a full redirect (anchor href) to Passport rather
  // than the in-page modal — the embedded SERP pane can't host the modal +
  // return-to-apply flow. Standalone VJP (mobile + desktop) keeps the modal.
  const isRedirectGate = isLoginGateActive && isDesktopEmbedded;
  const loginGateHref = isRedirectGate ? contextualSignInPassportURL : undefined;

  // Check URL for third-party intercept flow (user returning from passport auth redirect)
  useEffect(() => {
    if (hasProcessedUrlParam.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get("from");

    // If user is logged in and came from third-party intercept, show success modal
    if (isLoggedIn && applyLink && fromParam === "jsfe-3pintercept-mobviewjob") {
      hasProcessedUrlParam.current = true;
      setIsSuccessModalOpen(true);
    }
  }, [isLoggedIn, applyLink]);

  const handleExit = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const handleSuccessModalClose = useCallback(() => {
    setIsSuccessModalOpen(false);
    // Reload page to update logged-in state in UI post Passport Log In
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("from");
      window.location.href = url.toString();
    }
  }, []);

  const handleAuthSuccess = useCallback(() => {
    setIsLoginModalOpen(false);

    // Set URL param to indicate third-party intercept flow
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("from", "jsfe-3pintercept-mobviewjob");
      window.history.replaceState({}, "", url.toString());
    }

    setIsSuccessModalOpen(true);
  }, []);

  const onPressApply = useCallback(async () => {
    if (isRedirectGate) {
      // The anchor href navigates to Passport; fire apply logging in the
      // background and do not open the in-page modal.
      void onApply();
      return;
    }
    // onApply() fires best-effort apply logging; if it rejects we still open
    // the Passport gate (the button's primary job), so set the modal in finally.
    try {
      await onApply();
    } finally {
      if (isLoginGateActive) {
        setIsLoginModalOpen(true);
      }
    }
  }, [isRedirectGate, isLoginGateActive, onApply]);

  const modal = useMemo(() => {
    // Desktop 2-pane gates via the redirect anchor above, so there is no
    // in-page modal to render.
    if (isDesktopEmbedded) {
      return null;
    }
    // Standalone VJP (mobile + desktop) gates via the in-page Passport modal.
    // The modal opens GNAV's login (window._INDEED_GNAV.loginModal) and, on
    // success, shows SuccessfulSignInModal with the continue-to-apply link —
    // keeping the user in context (no full-page reload). If GNAV isn't
    // available, PassportLoginModal falls back to redirecting to
    // contextualSignInPassportURL.
    return (
      <>
        {isLoginGateActive && (
          <PassportLoginModal
            isOpen={isLoginModalOpen}
            onExit={handleExit}
            onAuthSuccess={handleAuthSuccess}
            contextualSignInPassportURL={contextualSignInPassportURL}
          />
        )}
        <SuccessfulSignInModal
          isOpen={isSuccessModalOpen}
          onClose={handleSuccessModalClose}
          thirdPartyUrl={applyLink ?? null}
          mobtk={mobtk}
          jobKey={jobKey}
        />
      </>
    );
  }, [
    isDesktopEmbedded,
    isLoginGateActive,
    isLoginModalOpen,
    isSuccessModalOpen,
    handleExit,
    handleAuthSuccess,
    handleSuccessModalClose,
    contextualSignInPassportURL,
    applyLink,
    mobtk,
    jobKey,
  ]);

  return {
    isLoginGateActive,
    // Desktop 2-pane redirects via this anchor href; standalone VJP opens the
    // in-page modal instead, so loginGateHref is undefined there.
    loginGateHref,
    onPressApply,
    modal,
  };
}

const PassportLoginModal: FC<PassportLoginModalProps> = ({
  isOpen,
  onExit,
  fromParameter = "viewjob-3pintercept",
  onAuthSuccess,
  branding = Branding.THIRD_PARTY_APPLIES,
  contextualSignInPassportURL,
}) => {
  const closePassportModal = useCallback(() => {
    window._INDEED_GNAV?.loginModal?.close();
  }, []);

  const memoizedGetParams = useMemo(() => {
    const onLoginSuccess = (e: CustomEvent) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      closePassportModal();
      void onAuthSuccess?.(e);
    };

    return {
      onOpenChange: onExit,
      fromParameter,
      onLoginSuccess,
      extraParameters: { branding },
    };
  }, [onExit, fromParameter, branding, onAuthSuccess, closePassportModal]);

  useEffect(() => {
    if (isOpen) {
      if (window._INDEED_GNAV?.loginModal?.open) {
        window._INDEED_GNAV.loginModal.open(memoizedGetParams);
      } else if (contextualSignInPassportURL) {
        globalThis.location.href = contextualSignInPassportURL;
      } else {
        console.error("PassportLoginModal: No contextualSignInPassportURL available for fallback");
        onExit();
      }
    }
  }, [isOpen, contextualSignInPassportURL, memoizedGetParams, onExit]);

  return null;
};

export default PassportLoginModal;
